import {
  useQuery,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import clsx from 'clsx';
import { useMemo, useState, useEffect } from 'react';

import { Skeleton } from '@dynatrace/strato-components/content';
import {
  type AriaLabelingProps,
  type DataTestId,
  type StylingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';
import { getAppId } from '@dynatrace-sdk/app-environment';

import * as styles from './AppIcon.sty.js';
import { FallbackIcon } from './FallbackIcon.js';
import { fetchIcon } from './icon-utils.js';
import { isProduction } from '../../../core/utils/is-node-environment.js';

// #region Typings
/**
 * Accepted properties for the AppIcon.
 * @public
 */
export interface AppIconProps
  extends AriaLabelingProps,
    WithChildren,
    StylingProps,
    DataTestId {
  /**
   * The path to the app icon. If not specified, the icon from the app registry is retrieved automatically.
   */
  src?: string;
  /**
   * The alternative text representation for the image when it's not loaded. If not specified, the app name is used.
   */
  alt?: string;
}
// #endregion

const AppIconRoot = (props: AppIconProps) => {
  const [fadeIn, setFadeIn] = useState(false);

  const sdkAppId = useMemo(getAppId, []);
  const {
    src = `/platform/app-engine/registry/v1/app-icons/${sdkAppId}`,
    alt,
    children,
    className: consumerClassName,
    style: consumerStyle = {},
    'data-testid': dataTestId,
    ...remainingProps
  }: AppIconProps = props;
  const {
    data: iconSrc,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['app-icon', src],
    queryFn: () => fetchIcon(src),
    retry: isProduction() ? undefined : false,
  });

  useEffect(() => {
    if (isLoading) {
      setFadeIn(isLoading);
    }
  }, [isLoading]);

  if (isLoading) {
    return <Skeleton width={20} height={20} />;
  }

  if (isError) {
    return <FallbackIcon size={20} />;
  }

  return (
    <img
      src={iconSrc}
      data-testid={dataTestId}
      width={20}
      height={20}
      alt={alt}
      className={clsx(styles.icon({ fadeIn }), consumerClassName)}
      style={consumerStyle}
      {...remainingProps}
    />
  );
};

const appIconQueryClient = new QueryClient();

/**
 * The `AppIcon` component renders the icon used for the app on the platform.
 * You can also provide a link to an icon to render that icon instead.
 * @public
 */
export const AppIcon = (props: AppIconProps) => {
  return (
    <QueryClientProvider client={appIconQueryClient}>
      <AppIconRoot {...props} />
    </QueryClientProvider>
  );
};

AppIcon.displayName = 'AppHeader.AppIcon';
